import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TextField } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faKey, faMobileScreen, faPlug, faRotateRight, faSimCard, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import StepFooter from './StepFooter';
import { FIELD_SX } from '../steps';
import { startWhatsAppOAuth } from '../../../utils/whatsappOAuth';
import { WA_FEATURE_TYPE_CLASSIC, WA_FEATURE_TYPE_COEXISTENCE } from '../../../components/WhatsAppSetupModal';
import './StepWhatsApp.css';

const API = process.env.REACT_APP_API_BASE_URL;

// The three ways a business brings its number (same choices as the Channels page's WhatsAppSetupModal):
// keep it on the WhatsApp Business app (Coexistence), register a separate number, or connect a Cloud API number.
const OPTIONS = [
  { key:'coexistence', icon:faMobileScreen, featureType:WA_FEATURE_TYPE_COEXISTENCE, title:'wa_setup_existing_title', desc:'wa_setup_existing_desc' },
  { key:'classic', icon:faSimCard, featureType:WA_FEATURE_TYPE_CLASSIC, title:'wa_setup_new_title', desc:'wa_setup_new_desc' },
  { key:'manual', icon:faKey, title:'wa_setup_manual_title', desc:'wa_setup_manual_desc' },
];

export default function StepWhatsApp({ botId, goNext, returnPath, busy: saving }) {
  const { t } = useTranslation();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState('');
  const [choice, setChoice] = useState('coexistence');
  const [manual, setManual] = useState({ phone_number_id:'', access_token:'', waba_id:'' });
  const stopOAuth = useRef(null);
  useEffect(() => () => { stopOAuth.current?.(); stopOAuth.current = null; }, [botId]);
  const refresh = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const response = await fetch(`${API}auth/whatsapp/status?bot_id=${encodeURIComponent(botId)}`);
      const data = await response.json();
      if (!response.ok) throw new Error(typeof data.detail === 'string' ? data.detail : t('wix_whatsapp_status_error'));
      setStatus(data);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [botId, t]);
  useEffect(() => { refresh(); }, [refresh]);
  const connections = status?.connections || [];
  const connected = connections.filter(item => item.status === 'active');
  const incomplete = !connected.length && connections.some(item => item.status === 'setup_incomplete');
  const option = OPTIONS.find(item => item.key === choice);

  function connectWithMeta() {
    if (connecting || saving) return;
    setConnecting(true); setError('');
    stopOAuth.current?.();
    stopOAuth.current = startWhatsAppOAuth({ botId, returnPath, featureType:option.featureType,
      openingLabel:t('wix_whatsapp_connecting'), onStatus:setStatus, onError:message => setError(t(message)),
      onDone:() => setConnecting(false) });
  }
  async function connectManual(event) {
    event.preventDefault();
    if (connecting || saving) return;
    setConnecting(true); setError('');
    try {
      const response = await fetch(`${API}messaging_connections/whatsapp/manual`, { method:'POST',
        headers:{ 'Content-Type':'application/json' },
        body:JSON.stringify({ bot_id:botId, phone_number_id:manual.phone_number_id.trim(), access_token:manual.access_token.trim(),
          waba_id:manual.waba_id.trim() || null, auto_response_mode:'respond_all', pause_on_manual_reply:true }) });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || !result.success) throw new Error(typeof result.detail === 'string' ? result.detail : t('social_whatsapp_complete_failed'));
      setManual({ phone_number_id:'', access_token:'', waba_id:'' });
      await refresh();
    } catch (e) { setError(e.message); }
    finally { setConnecting(false); }
  }
  function cancelConnect() {
    stopOAuth.current?.(); stopOAuth.current = null;
    setConnecting(false);
  }
  function connectLater() {
    cancelConnect(); goNext();
  }

  const metaUnavailable = !loading && status && !status.configured;
  return <>
    <header className="ac-head">
      <h2 className="ac-title ac-whatsapp-title"><img src="/whatsapp-logo.webp" alt="" aria-hidden="true" />{t('wix_whatsapp_title')}</h2>
      <p className="ac-sub">{t('wix_whatsapp_sub')}</p>
    </header>
    <div className="ac-body ac-whatsapp-body">
      {connected.length > 0 ? <section className="ac-whatsapp-account is-connected" aria-live="polite">
        <img className="ac-whatsapp-logo" src="/whatsapp-logo.webp" alt="WhatsApp" />
        <div className="ac-whatsapp-copy">
          <h3>{t('wix_whatsapp_connected')}</h3>
          <p>{connected.map(item => [item.display_name, item.phone_number].filter(Boolean).join(' · ') || 'WhatsApp').join(', ')}</p>
          <span className="ac-whatsapp-badge"><FontAwesomeIcon icon={faCheck} /> {t('wix_whatsapp_live')}</span>
        </div>
      </section> : <>
        <fieldset className="ac-whatsapp-options" disabled={connecting || saving}>
          <legend>{t('wix_whatsapp_choose')}</legend>
          {OPTIONS.map(item => <label key={item.key} className={`ac-whatsapp-option ${choice === item.key ? 'is-selected' : ''}`}>
            <input type="radio" name="whatsapp-setup" value={item.key} checked={choice === item.key} onChange={() => setChoice(item.key)} />
            <span className="ac-whatsapp-option-icon" aria-hidden="true"><FontAwesomeIcon icon={item.icon} /></span>
            <span className="ac-whatsapp-option-copy"><strong>{t(item.title)}</strong><span>{t(item.desc)}</span></span>
          </label>)}
        </fieldset>
        {choice === 'manual' ? <form className="ac-whatsapp-manual" onSubmit={connectManual}>
          <p>{t('wa_setup_manual_intro')}</p>
          <TextField size="small" sx={FIELD_SX} label={t('wa_setup_manual_phone_id')} value={manual.phone_number_id} required
            onChange={e => setManual(m => ({ ...m, phone_number_id:e.target.value }))} inputProps={{ inputMode:'numeric', autoComplete:'off' }} />
          <TextField size="small" sx={FIELD_SX} label={t('wa_setup_manual_waba_id')} value={manual.waba_id}
            onChange={e => setManual(m => ({ ...m, waba_id:e.target.value }))} inputProps={{ inputMode:'numeric', autoComplete:'off' }} />
          <TextField size="small" sx={FIELD_SX} type="password" label={t('wa_setup_manual_token')} value={manual.access_token} required
            onChange={e => setManual(m => ({ ...m, access_token:e.target.value }))} inputProps={{ autoComplete:'off' }} />
          <button type="submit" className="ac-btn-primary" disabled={connecting || saving || !manual.phone_number_id.trim() || !manual.access_token.trim()}>
            {connecting ? <span className="ac-spinner ac-spinner-sm ac-spinner-light" /> : <FontAwesomeIcon icon={faPlug} />}
            {t('wa_setup_manual_submit')}
          </button>
        </form> : <div className="ac-whatsapp-connect">
          <p>{t(choice === 'coexistence' ? 'wix_whatsapp_meta_hint_existing' : 'wix_whatsapp_meta_hint_new')}</p>
          <button type="button" className="ac-btn-primary" onClick={connectWithMeta} disabled={loading || connecting || saving || metaUnavailable}>
            {connecting ? <span className="ac-spinner ac-spinner-sm ac-spinner-light" /> : <FontAwesomeIcon icon={faPlug} />}
            {t(connecting ? 'wix_whatsapp_connecting' : 'wix_whatsapp_connect')}
          </button>
        </div>}
      </>}
      {incomplete && <div className="ac-whatsapp-warning" role="status"><FontAwesomeIcon icon={faTriangleExclamation} /><span>{t('wix_whatsapp_incomplete')}</span></div>}
      {connecting && choice !== 'manual' && <div className="ac-whatsapp-status" role="status">
        <span>{t('wix_whatsapp_popup_waiting')}</span>
        <button type="button" className="ac-btn-ghost" onClick={cancelConnect}>{t('cancel')}</button>
      </div>}
      {loading && <div className="ac-whatsapp-status" role="status"><span className="ac-spinner ac-spinner-sm" />{t('loading')}</div>}
      {metaUnavailable && !connected.length && choice !== 'manual' && <div className="ac-error" role="alert">{t('wix_whatsapp_unavailable')}</div>}
      {error && <div className="ac-error" role="alert">{error}</div>}
      {(error || incomplete) && <button type="button" className="ac-btn-ghost" onClick={refresh} disabled={loading}><FontAwesomeIcon icon={faRotateRight} />{t('wix_entry_retry')}</button>}
    </div>
    <StepFooter primaryLabel={t('wxo_continue')} onPrimary={goNext}
      onSkip={!connected.length ? connectLater : undefined} skipLabel={t('wix_whatsapp_connect_later')}
      disabled={!connected.length || loading || connecting} busy={saving} />
  </>;
}
